import React, { useState, useEffect } from 'react';
import CardDetails from "./details";
import PanelList from "./panelList";
import ListZeroState from '../listZeroState';
import Modal from 'react-modal';
import DeleteContactModal from './deleteContactModal';
import ObjectMerge from 'object-merge';
import PropTypes from 'prop-types';
import { getIntlText, phoneNumberValidation } from '../../js/utilities';
import styled from 'styled-components';
import useContacts from "../../hooks/contacts";
import useAlerts from '../../hooks/alerts';
import { track } from 's2s-app-service';
import { colorGrid } from "s2s-themes";

const ContactDetailsContainer = styled.div`
  background-color: ${colorGrid.gray0};
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const reactModalStyle = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(34, 138, 230, 0.72)',
    zIndex: '1000'
  },
  content: {
    alignItems: 'center',
    position: 'absolute',
    top: '0px',
    left: '0px',
    right: '0px',
    bottom: '0px',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'center',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    borderRadius: '4px',
    outline: 'none',
    padding: '20px'
  }
};

const ContactDetailsCard = (props) => {
  const [, currentContact, , { getContact }] = useContacts();
  const [, { createErrorAlert }] = useAlerts();
  // on initial render fetchContacts
  useEffect(() => {
    getContact(props.contactUUID, props.intl);
  }, []);

  Modal.setAppElement('#root'); // some accessibility stuff react-modal wants defined or else throws a warning. http://reactcommunity.org/react-modal/accessibility/#app-element - KCN 03/13/19 
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Make a call to reroute to ContactEditForm
  const displayEditScreen = () => {
    track("edit_contact", "clicked");
    props.cbEditContact(currentContact);
  };

  // Calling a contact
  const handleMakeCallClick = (data) => {
    track("make_call", "clicked");
    // validate phone number before trying to make call
    if (phoneNumberValidation(data.number)) {
      props.cbContactDetailsClick('make_call', data.number);
    } else {
      createErrorAlert({
        message: getIntlText(props.intl, 'CONTACTS-ALERT-BODY-PHONE-NUMBER-INVALID', "The contact phone number you tried to call is not valid. Please edit contact to correct error."),
        details: [getIntlText(props.intl, 'CONTACTS-ALERT-HEADING-PHONE-NUMBER-INVALID', "Contact Phone Number Is Not Valid")],
        autoCloseTimeout: 10,
      });
    }

    console.log('handleMakeCallClick data', data);
  };

  // function that displays PanelList if user has contact info or EmptyListZeroState if user does not have contact info
  const togglePanelListListZeroScreen = (entityDetails = {}) => {
    if (entityDetails && entityDetails.phone_numbers !== undefined) {
      return (
        <PanelList
          cbItemClicked={handleMakeCallClick}
          cbPhoneNumberValidation={phoneNumberValidation}
          panelItemList={ObjectMerge({}, entityDetails)}
        />
      );
    } else {
      return (
        <ListZeroState cbButtonClick={displayEditScreen} />
      );
    }
  };

  const renderCardDetails = (currentContact = {}, intl) => {
    return (
      <React.Fragment>
        <CardDetails
          cbDelete={toggleModal}
          cbEdit={displayEditScreen}
          entityDetails={ObjectMerge({}, currentContact)}
          intl={intl}
        />
        {togglePanelListListZeroScreen(currentContact)}
      </React.Fragment>
    );
  };

  // Opening/closing a modal
  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  // Deleting a contact, closing delete contact modal
  const confirmContactDelete = (cbContactDetailsClick, entityDetails = {}) => {
    cbContactDetailsClick('delete_contact', entityDetails.uuid);
    toggleModal();
  };

  const renderModal = (intl) => {
    if (modalIsOpen === true) {
      return (
        <Modal
          className="contactDetailsModal"
          contentLabel="contactDetailsModal"
          isOpen={modalIsOpen}
          style={reactModalStyle}
        >
          <DeleteContactModal
            cbCancelClick={toggleModal}
            cbConfirmDelete={() => { track("contact_delete", "clicked"); confirmContactDelete(props.cbContactDetailsClick, currentContact); }}
            headerText={getIntlText(intl, 'CONTACT-DETAILS-CARD-MODAL-HEADER-DELETE-CONTACT', "Are you sure you want to delete this contact?")}
            descriptionText={getIntlText(intl, 'CONTACT-DETAILS-CARD-MODAL-DESCRIPTION-DELETE-CONTACT', "Completing this action will permanently remove the current contact’s information. ")}
          />
        </Modal>
      );
    }
  };

  return (
    <ContactDetailsContainer className='contactDetailsContainer' >
      {renderModal(props.intl)}
      {renderCardDetails(currentContact, props.intl)}
    </ContactDetailsContainer>
  );
};

ContactDetailsCard.propTypes = {
  cbContactDetailsClick: PropTypes.func,
  cbEditContact: PropTypes.func,
  contactUUID: PropTypes.string,
  intl: PropTypes.object,
};

ContactDetailsCard.defaultProps = {
  cbContactDetailsClick: () => { console.log('cbContactDetailsClick is not defined. '); },
  cbEditContact: () => { console.log('cbEditContact is not defined. '); },
  contactUUID: undefined
};

export default ContactDetailsCard;
